import {FaqBase, FaqSection} from './faq-base';

export class FaqContent extends FaqBase
{
    public getFaqs(): FaqSection[]
    {
        const walkthroughUrl = this.domainUrl + '/app' + this.routes.PUBLIC.SIGNUPWALKTHROUGH.path;
        return [
            {
                sectionName: 'Sign Up',
                faqs: [
                    {
                        question: `What is ${this.instanceName}?`,
                        answer: `${this.instanceName} is a payment solution platform powered by Pay@ that simplifies the
                            payment journey for businesses and customers. Merchants and billers can easily send
                            payment requests to their customers, access auto-generated invoices, leverage QR code
                            payments, and submit bulk payment requests. ${this.instanceName}, through our partnership with 
                            Pay@, offers the largest payment network in South Africa.`
                    },
                    {
                        question: `What is ${this.instanceName}'s relationship to Pay@ Services?`,
                        answer: `${this.instanceName} is a platform developed by Pay@ Services (Pty) Ltd, a registered 
                        financial service provider (FSP No. 29423). ${this.instanceName} is a powerful front-end
                        tool specifically geared for the SME market that makes use of the existing, robust bill
                        aggregation and payment processing services that Pay@ provides to its customers. Pay@ is
                        a leading bill payment aggregator in South Africa.`
                    },
                    {
                        question: `Where do I sign-up for ${this.instanceName}?`,
                        answer: `To register for ${this.instanceName}, simply head to
                         <a href="${this.domainUrl}" target="_blank">
                             ${this.domainUrl}
                         </a> and complete the straightforward sign-up process on the website.`
                    },
                    {
                        question: `How do I sign up my organisation to ${this.instanceName}?`,
                        answer: `To sign up for ${this.instanceName}, simply click "Sign Up" and follow the steps.
                        Regardless of your organisation type (PTY Ltd, CC, or Sole Proprietor), you'll create
                        the business profile and a main user account simultaneously. Provide the organisation
                        and main user details, accept T&Cs and privacy policy. Then, complete your
                        organisation's details under the Account Settings tab. Make sure your information is
                        accurate as this is required to perform KYC(FICA) validation and is linked to the
                        auto-generated invoices and payments. Your organisation's full details will be required
                        to transact using ${this.instanceName}. `
                    },
                    {
                        question: `Do you have a User Guide that will assist me when signing up?`,
                        answer: `Check out our handy
                        <a href="${walkthroughUrl}" target="_blank">
                            walkthrough
                        </a>
                        it’s got everything you need to get started!`
                    },
                    {
                        question: `What information should I keep at hand when signing up for ${this.instanceName}?`,
                        answer: `When signing up for ${this.instanceName}, you should have your business, banking and
                        address details ready. You will be able to update these details under Account Settings
                        after signing up and once verification is complete.`
                    },
                    {
                        question: `What steps do I need to follow after my information is validated to start
                        using ${this.instanceName}?`,
                        answer: `Once your information is validated, you can start using ${this.instanceName}. Log in 
                        and check that all your information is correctly entered under the Account Settings menu option.
                        Once setup is completed, you can send payment requests, track payments, and manage your 
                        collections effortlessly. `
                    },
                    {
                        question: `Can I use ${this.instanceName} on my computer and my smartphone?`,
                        answer: `Yes, ${this.instanceName} is accessible on both your computer and smartphone via
                        <a href="${this.domainUrl}" target="_blank">${this.domainUrl}</a>, 
                        providing convenience no matter where you are.`
                    },
                    {
                        question: `Do I need to undergo bank verifications for ${this.instanceName}?`,
                        answer: `Yes, all new billers are required to undergo bank verification and checks. But don’t
                        worry, we will do this for you, just make sure you enter in your correct bank details
                        under the Account Settings menu option on your first login. If your bank details change
                        in the future, you can update them again.`
                    },
                    {
                        question: `After signing up for ${this.instanceName}, how quickly can I begin using the
                        solution?`,
                        answer: `Once all the necessary checks are completed and you receive your login details, you can
                        start using the platform right away.`
                    },
                    {
                        question: `What are the key advantages of using ${this.instanceName} for businesses?`,
                        answer: `The primary benefits include streamlined payment procedures with optional automated
                        invoice generation, automated management of cash flow and reconciliation. ${this.instanceName} 
                        eliminates the need for advanced back-end systems and supports payments across various methods.`
                    },
                    {
                        question: `How do I upload my organisation's logo to reflect on my invoices?`,
                        answer: `To add your organisation's logo, go to the Account Settings page. Scroll down to the
                        "Profile" section and select "Upload" under "Company Logo". Make sure the file format is .png 
                        or .jpg and the size is no larger than 500kb.`
                    },
                    {
                        question: `I made an error during the ${this.instanceName} registration process by entering my
                        information incorrectly. Can I correct it?`,
                        answer: `Yes, you can. However, note that editing your information will require re-verification
                        and checks.`
                    }
                ]
            },
            {
                sectionName: 'Network Partner',
                faqs: [
                    {
                        question: `Can my customers use ${this.instanceName} to settle their bills at retailers for
                        payments?`,
                        answer: `Yes, customers can pay in-store at any till at Shoprite, Checkers and Usave.`
                    },
                    {
                        question: `How does ${this.instanceName} work for in-store payments?`,
                        answer: `${this.instanceName} enables in-store payments through its extensive network of retailers.
                        Customers have the convenience of paying for products or services at participating stores by
                        simply presenting the GetPaid/Pay@ generated reference number for the bill or the invoice 
                        barcode at any till point.`
                    },
                    {
                        question: `Who are ${this.instanceName}’s partners for digital transactions?`,
                        answer: `${this.instanceName} has partnerships with various digital transaction partners ranging
                        from online card, banking apps, payment apps, EFT, and various other options. Partners include
                        the likes of Scan to Pay, SnapScan, Zapper, certain banking apps (such as Nedbank Scan-to-Pay).
                        A complete list is available <a href="https://mymoneymarket.co.za/getpaid" target="_blank">
                         here</a>`
                    },
                    {
                        question: `Which payment methods are available for your customers to use when paying for your
                        services?`,
                        answer: `Customers have the option to pay via card (debit and credit cards) or cash. The payment
                        networks/methods encompass: (1) card-based mobile apps like Scan to Pay, SnapScan, Zapper,
                        certain banking apps (such as Nedbank Money App), (2) 3D Secure using your Visa or
                        Mastercard, or (3) payment at retailers, where customers can use cash or cards. Retailers
                        currently include Shoprite, Checkers and Usave`
                    },
                ]
            },
            {
                sectionName: 'Technical',
                faqs: [
                    {
                        question: `Is integration with other back-end systems possible?`,
                        answer: `No advanced back-end systems or integrations are necessary with ${this.instanceName}, 
                        although integration is an option if needed using the webhook feature for payment notifications.
                        This can be found under the Account Settings menu option.`
                    },
                    {
                        question: `What payment methods are supported by ${this.instanceName}?`,
                        answer: `${this.instanceName} supports payments anywhere, anytime, via online, in-store, or 
                        digital transactions.`
                    },
                    {
                        question: `Will I be notified when a payment has been made to me?`,
                        answer: `Instant notifications will be sent to you upon receiving payments through 
                        ${this.instanceName}. Be sure to activate SMS notifications when sending payment requests to your 
                        customers. Alternatively, you can use the webhook feature for system-to-system notifications. 
                        You will receive instant confirmation of your customer's payment at the time they make it. This
                        notification can be accessed under the Status column in the Dashboard. Additionally, you can
                        click on a specific payment request to view the audit trail associated with that request`
                    },
                    {
                        question: `Can I send payment requests in bulk with ${this.instanceName}?`,
                        answer: `Yes, you can upload your payment requests in bulk, solving month-end billing needs. 
                        Note that you will need to request for the bulk payment capability to be activated via our Get
                        Help button when logged in or via our query management portal.`
                    },
                    {
                        question: `How do I monitor my payments on ${this.instanceName}?`,
                        answer: `You can keep track of payments using the user-friendly dashboards provided by 
                        ${this.instanceName}.`
                    },
                    {
                        question: `Should I encounter a problem on sign-up, what technical support does
                         ${this.instanceName} offer?`,
                        answer: `Technical support is available for any sign-up or platform-related issues.Simply select
                        the Get Help option from the menu.`
                    },
                    {
                        question: `How can I manage my reports and reconciliation?`,
                        answer: `You can easily download reconciliation reports and review settlements on 
                        ${this.instanceName} via the Settlement menu option. Visit your settlement dashboard and click 
                        on the Recon File button.`
                    },
                    {
                        question: `Is ${this.instanceName} exclusively for business transactions, or can it be used for 
                        personal purposes as well?`,
                        answer: `${this.instanceName} is tailored for businesses engaging in commercial transactions, 
                        offering a seamless payment experience for both businesses and their customers. 
                        ${this.instanceName} can only be used for person to business and business to business payments.`
                    },
                    {
                        question: `When will I receive the funds after a payment is made to me?`,
                        answer: `The default settlement period is T+2 business days (1-3 days depending on timing of 
                        when your customers make payment) after a transaction was processed. However, this may vary
                        depending on your business's risk rating.`
                    },
                    {
                        question: `Can I use my personal banking details when registering for ${this.instanceName}?`,
                        answer: `Yes, at ${this.instanceName}, we recognise that many sole proprietors operate from their
                        personal accounts, and we accommodate them for this purpose.`
                    },
                    {
                        question: `Can I share my login details with an employee?`,
                        answer: `Sharing login details is not recommended for the ${this.instanceName} site.
                        However, ${this.instanceName} does offer the option to grant access to other users within the
                        business, such as field agents or administrative staff. They would need to have their own
                        login credentials.`
                    },
                    {
                        question: `If I can’t share my login details, how do I set up users and assign roles to users?`,
                        answer: `To configure users and allocate roles within ${this.instanceName}, go to the "User" 
                        screen located in the "Account Details" section. Different roles provide varying levels of 
                        access, ranging from super user (complete access) to administrator (limited access) to field
                        agents (restricted access). When adding a user, designate the suitable role to specify their
                        permissions.`
                    },
                    {
                        question: `I have completed the signup process successfully, but now I have additional 
                        questions.`,
                        answer: `You can submit your inquiry through our support portal via the ${this.instanceName}
                        platform.`
                    },
                    {
                        question: `Can I send reminders to customers who haven't made a payment yet?`,
                        answer: `Certainly, you can simply access your dashboard, choose the payment request you wish to
                        send, and select "send" to resend a payment request to your customer`
                    },
                    {
                        question: `What are the terms and conditions relating to the use of ${this.instanceName}?`,
                        answer: `To access our General Terms and Conditions and our Privacy Policy, refer to your 
                        sign-up email or access these via the menu once logged into ${this.instanceName}. Click
                        <a href="${this.serviceTermsLink}" target="_blank">here</a> to view.`
                    },
                    {
                        question: `My question has not been addressed in the FAQ's.`,
                        answer: `If your question has not been answered, please select "Get Help" from the menu.`
                    },
                    {
                        question: `Can I cancel a payment request?`,
                        answer: `Yes, payment requests can be cancelled, but only before your customer submits payment.
                        Once paid, ${this.instanceName} cannot process transaction reversals. To cancel a payment request,
                        go to the dashboard and click the cancel icon next to the specific request. Note that if you
                        need to refund your customer, you will have to do this outside of the ${this.instanceName}
                        platform.`
                    },
                    {
                        question: `How can I generate a payment request?`,
                        answer: `Once you've provided a service to your customer, you can easily create a payment 
                        request by clicking on 'Request payment' and entering the necessary service details 
                        (customer name, service description, unique invoice number, amount). Please ensure that the 
                        amount entered in the Amount field includes VAT if your organisation is VAT registered. If your
                        organisation is VAT registered, you would have already added the VAT number in Account Settings.
                        The Tax Invoice sent to your customer will automatically calculate the VAT amount and display 
                        your VAT number.`
                    },
                    {
                        question: `How can I monitor the progress of payment requests sent to customers?`,
                        answer: `Access the Dashboard to track and review the status of each payment request. Clicking
                        on individual payment requests provides a detailed audit trail specific to that request.`
                    },
                    {
                        question: `How do I upload and send multiple payment requests for multiple customers (bulk 
                        upload)?`,
                        answer: `Organisations have the option to extract details of numerous payment requests from 
                        their back-end accounting systems into a standardised ${this.instanceName} CSV File format. This 
                        CSV File can be uploaded to ${this.instanceName} using the “Upload bulk payment file” feature 
                        within the “Payment” section. Users can review the line items before importing the file (the
                        uploaded file is visible on the ${this.instanceName} screen) and once imported, the file is
                        processed within ${this.instanceName}. Upon pressing the “Import” button, multiple payment
                        requests are automatically dispatched to customers via email and/or sms.`
                    },
                    {
                        question: `How do I send the payment request to my customer?`,
                        answer: `Once you have created the payment request there will be 3 options to present the 
                        payment request to your customer, namely – (1) a QR code is displayed which your customer can
                        scan and pay with their preferred QR payment app (see payment method FAQ) for when your customer
                        is present. If your customer is not present at the place at which you provided the service,
                        you can (2) send a payment link to your customer by SMS or (3) email a payment request to
                        your customer with an attached tax invoice.`
                    },
                    {
                        question: `How do I send a tax invoice to my customer?`,
                        answer: `Once you create a payment request, a tax invoice is automatically generated. If you opt
                        for email delivery for your payment request, the tax invoice (PDF) is automatically attached to
                        the email and sent to your customer.`
                    },
                    {
                        question: `Is it necessary to include a tax invoice with every payment request I create?`,
                        answer: `No, you have the option to use the Quick Pay function to send an SMS or email payment
                        request (link) without the need to create a tax invoice`
                    },
                    {
                        question: `How do I link my back-end accounting or billing (ERP type) system to 
                        ${this.instanceName}?`,
                        answer: `No formal back-end systems or integrations are necessary with ${this.instanceName}, 
                        although integration is an option if needed using the webhook feature for payment notifications.
                        This can be found under the Account Settings menu option. However, we are currently developing 
                        an integrated link to allow back-end accounting systems to utilise ${this.instanceName} for
                        presenting invoices and handling payments. Stay tuned for more information! In the meantime,
                        please refer to the previously mentioned 'bulk upload' FAQ for the current process of extracting
                        information from back-end systems and uploading it to ${this.instanceName} for processing.`
                    },
                ]
            },
            {
                sectionName: 'Fees',
                faqs: [
                    {
                        question: `How much does it cost to use ${this.instanceName}?`,
                        answer: `You will be charged a fee for each payment transaction processed. These fees are
                        deducted from each transaction automatically. So, the payment you receive will be the amount 
                        left after the GetPaid fees are deducted – this is called a “net settlement.” There are no costs
                        for signing up or integrating. Please go to:
                        <a href="https://mymoneymarket.co.za/getpaid" target="_blank">
                        https://mymoneymarket.co.za/getpaid</a> for more information on fees.`
                    }
                ]
            }
        ];
    }
}